// src/components/admin/DashboardView.jsx

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Music, Video, Image as ImageIcon, Users, ShoppingBag, ShieldCheck } from 'lucide-react';

export default function DashboardView({ music, videos, members, gallery }) {
  const cards = [
    { id: 'music', icon: Music, title: 'Music', value: music.length, sub: 'Gospel tracks live on the platform', hint: 'Manage music' },
    { id: 'videos', icon: Video, title: 'Videos', value: videos.length, sub: 'Worship and performance videos', hint: 'Manage videos' },
    { id: 'members', icon: Users, title: 'Members', value: members.length, sub: 'The AsidorS family members', hint: 'Manage members' },
    { id: 'gallery', icon: ImageIcon, title: 'Gallery', value: gallery.length, sub: 'Captured moments and photos', hint: 'Manage gallery' },
    { id: 'purchases', icon: ShoppingBag, title: 'Purchases', value: '—', sub: 'Track sales and payment activity', hint: 'View purchases' },
    { id: 'approving', icon: ShieldCheck, title: 'Approvals', value: '—', sub: 'Review pending artist submissions', hint: 'Review requests' },
  ];

  return (
    <div className="relative">
      {/* Decorative accents */}
      <div
        className="pointer-events-none absolute top-28 right-6 w-24 h-24 rounded-full bg-[#bd9f67]/10 blur-2xl"
        aria-hidden="true"
      ></div>
      <div
        className="pointer-events-none absolute top-10 right-16 h-12 border border-[#bd9f67]/30"
        aria-hidden="true"
      ></div>

      <h2 className="text-2xl md:text-3xl font-bold text-[#f9f9f9] mb-2 tracking-tight">
        Welcome back, Admin
      </h2>
      <p className="text-sm text-[#a6a6b5] mb-12">
        Manage your ministry's content, artists, and purchases from one place.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="group relative"
            >
              <Link to={`/admin?tab=${card.id}`} className="block h-full">
                <div className="relative flex flex-col justify-between h-64 p-5 rounded-[0.7rem] border border-[#bd9f67]/25 bg-[#243137] backdrop-blur-xl transition-all duration-300 overflow-hidden hover:border-[#bd9f67]/50 hover:shadow-[0px_0px_20px_1px_rgba(189,159,103,0.22)]">
                  {/* Card decorative circle */}
                  <div
                    className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-[#bd9f67]/10 transition-colors duration-300 group-hover:bg-[#bd9f67]/20"
                    aria-hidden="true"
                  ></div>
                  <div
                    className="absolute top-6 right-7 h-9 w-px bg-[#bd9f67]/40"
                    aria-hidden="true"
                  ></div>

                  {/* Top: icon + big number */}
                  <div className="relative">
                    <Icon size={22} className="text-[#bd9f67] mb-2" />
                    <div className="text-[2rem] font-medium text-white tracking-[0.1em]">
                      {card.value}
                    </div>
                  </div>

                  {/* Bottom: label + description + hint */}
                  <div className="relative">
                    <div className="block mb-2 text-lg font-medium text-white">{card.title}</div>
                    <p className="text-sm font-light text-white/60 tracking-wide mb-2">{card.sub}</p>
                    <span className="text-xs font-medium text-[#bd9f67]">{card.hint} →</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
